import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Home, Mail, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { updateMetaTag } from "@/lib/seo-utils";
import { updateCanonicalTag, getProductionUrl } from "@/lib/canonical-utils";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ScrollProgress } from "@/components/ScrollProgress";

interface LegalSection {
  id: string;
  section_title: string;
  section_content: string;
  display_order: number;
  last_updated: string;
}

const Privacy = () => {
  const [sections, setSections] = useState<LegalSection[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Hardcoded SEO for Privacy page
    document.title = "Privacy Policy | ShoreSight Marine Windows";
    
    const metaDescription = "ShoreSight Marine Windows privacy policy. Learn how we collect, use, and protect your personal information when you use our marine window services and website.";
    
    updateMetaTag("name", "description", metaDescription);
    updateMetaTag("property", "og:title", document.title);
    updateMetaTag("property", "og:description", metaDescription);
    updateMetaTag("property", "og:type", "website");
    updateMetaTag("name", "twitter:card", "summary");
    updateMetaTag("name", "twitter:title", document.title);
    updateMetaTag("name", "twitter:description", metaDescription);
    updateMetaTag("name", "robots", "noindex, follow");

    // Canonical and OG URL
    const canonicalUrl = getProductionUrl("/privacy");
    updateCanonicalTag(canonicalUrl);
    updateMetaTag("property", "og:url", canonicalUrl);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      // Fetch legal sections
      const { data: sectionsData } = await (supabase as any)
        .from("legal_sections")
        .select("*")
        .eq("page_id", "privacy")
        .order("display_order");
      
      if (sectionsData && sectionsData.length > 0) {
        setSections(sectionsData);
        // Get the most recent update date
        const dates = sectionsData.map((s: LegalSection) => new Date(s.last_updated));
        const mostRecent = new Date(Math.max(...dates.map(d => d.getTime())));
        setLastUpdated(mostRecent.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }));
      }
      
      setLoading(false);
    };
    
    fetchData();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <ScrollProgress />
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 py-16 md:py-20">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-40"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-azure-500/10 border border-azure-500/20 mb-6">
              <Shield className="w-8 h-8 text-azure-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Privacy Policy
            </h1>
            {lastUpdated && (
              <p className="text-azure-200 text-sm mb-4">
                Last Updated: {lastUpdated}
              </p>
            )}
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              At ShoreSight Marine Windows, we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your data.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main id="main-content" className="flex-1 py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">

            {/* Table of Contents */}
            {sections.length > 0 && (
              <nav className="mb-8 p-8 rounded-xl bg-gradient-to-br from-card to-card/50 border border-border shadow-lg">
                <h2 className="text-xl font-semibold text-foreground mb-6 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  Table of Contents
                </h2>
                <ol className="space-y-3">
                  {sections.map((section, index) => (
                    <li key={section.id}>
                      <a
                        href={`#section-${index + 1}`}
                        className="text-primary hover:text-azure-600 hover:underline transition-colors font-medium group flex items-start gap-2"
                      >
                        <span className="text-muted-foreground group-hover:text-primary transition-colors">{index + 1}.</span>
                        <span>{section.section_title}</span>
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            )}

          {/* Content Sections */}
          {loading ? (
            <div className="space-y-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="h-6 bg-muted rounded w-1/3 mb-3"></div>
                  <div className="h-4 bg-muted rounded w-full mb-2"></div>
                  <div className="h-4 bg-muted rounded w-5/6"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="prose prose-slate max-w-none">
              {sections.map((section, index) => (
                <section
                  key={section.id}
                  id={`section-${index + 1}`}
                  className="mb-8 scroll-mt-20"
                >
                  <h2 className="text-2xl font-semibold text-foreground mb-4">
                    {index + 1}. {section.section_title}
                  </h2>
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                    {section.section_content}
                  </p>
                </section>
              ))}
            </div>
          )}

            {/* Contact Section */}
            <div className="mt-12 p-8 rounded-xl bg-gradient-to-br from-navy-900 to-navy-800 border border-navy-700 shadow-xl">
              <h2 className="text-2xl font-semibold text-white mb-3 flex items-center gap-2">
                <Mail className="w-6 h-6 text-azure-400" />
                Contact Us About Privacy
              </h2>
              <p className="text-gray-300 mb-6">
                If you have questions about this Privacy Policy or want to exercise your privacy rights, please contact us:
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-gray-200">
                  <Mail className="w-5 h-5 text-azure-400 shrink-0" />
                  <a 
                    href="mailto:info@shoresightmarine.com"
                    className="hover:text-azure-400 transition-colors"
                  >
                    info@shoresightmarine.com
                  </a>
                </div>
                <div className="flex items-center gap-3 text-gray-200">
                  <Phone className="w-5 h-5 text-azure-400 shrink-0" />
                  <a 
                    href="tel:6193774673"
                    className="hover:text-azure-400 transition-colors"
                  >
                    (619) 377-4673
                  </a>
                </div>
                <div className="flex items-start gap-3 text-gray-200">
                  <MapPin className="w-5 h-5 text-azure-400 shrink-0 mt-0.5" />
                  <a 
                    href="https://maps.app.goo.gl/GyCUnxD8oekkuSK37"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-azure-400 transition-colors"
                  >
                    2608 Shelter Island Dr Suite 302<br />
                    San Diego, CA 92106
                  </a>
                </div>
              </div>
            </div>

            {/* Back to Home */}
            <div className="mt-8 text-center">
              <Link to="/">
                <Button variant="outline" className="gap-2">
                  <Home className="w-4 h-4" />
                  Back to Homepage
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Privacy;
